import { useEffect } from "react";
import DataTable, { Column } from "../../components/DataTable";
import { edit, trash } from "../../asset";
import { Group } from "../../types/groupAndHouses";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useDispatch } from "react-redux";
import {
  getGroups,
  setFilters,
  setPagination,
} from "../../features/group-houses/GroupSlice";
import GroupApis from "../../api/groupApis";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import useConfirmToast from "../../util/hooks/useConfirmToast";
import Tag from "../../components/Tags";
import PaginationBar from "../../components/PaginationBar";
import DownloadButton from "../../components/common/DownloadButton";
import { exportToExcel } from "../../util/functions/UniversalFunctions";
import { useBranchCode } from "../../util/hooks/useBranch";

export default function GroupList() {
  const dispatch = useDispatch<AppDispatch>();
  const { groups, isLoading, pagination, filters } = useSelector(
    (state: RootState) => state.group,
  );

  const navigate = useNavigate();

  const { confirm, modal } = useConfirmToast();
  const branchCode = useBranchCode();

  useEffect(() => {
    dispatch(
      getGroups({
        page: pagination.currentPage,
        size: pagination.pageSize,
        branchCode,
        name: filters?.name,
      }),
    );
  }, [
    dispatch,
    pagination.currentPage,
    pagination.pageSize,
    branchCode,
    filters,
  ]);

  const handleDelete = async (id: number) => {
    const confirmed = await confirm(
      "Are you sure you want to delete this group?",
    );

    if (!confirmed) return;

    try {
      await GroupApis.deleteGroup(id);
      toast.success("Group deleted successfully");
      dispatch(
        getGroups({
          page: pagination.currentPage,
          size: pagination.pageSize,
          branchCode,
        }),
      );
    } catch {
      toast.error("Failed to delete group");
    }
  };

  const columns: Column<Group>[] = [
    {
      label: "SL NO.",
      accessor: "groupId",
      render: (item, i) => <div>{i + 1}</div>,
    },
    {
      label: "Group/House Name",
      accessor: "name",
      searchable: true,
      render: (item) => (
        <div className="text-neutral-400 text-sm font-bold">
          {item?.groupName}
        </div>
      ),
    },
    {
      label: "No. of Students",
      accessor: "noOfStudents",
      render: (item) => (
        <div className="w-full">
          <span className="text-neutral-400 text-[13px]">
            {item.noOfStudents}
          </span>
        </div>
      ),
    },
    {
      label: "View Details",
      accessor: "viewDetails",
      render: (item) => (
        <Tag
          size="sm"
          text="View Details"
          onClick={() =>
            navigate(`/groups-houses/view-details/${item.groupId}`)
          }
        />
      ),
    },
    {
      label: "Edit Details",
      accessor: "edit",
      render: (item) => (
        <button
          onClick={() =>
            navigate(`/groups-houses/edit-group/${item.groupId}`, {
              state: {
                mode: "edit",
                group: item,
              },
            })
          }
          className="flex gap-2 w-fit px-2 py-1 items-center text-nowrap text-xs text-warning-700 bg-warning-50 rounded-[4px] font-medium"
        >
          <img src={edit} alt="icon" className="w-3 h-3" />
          <button>Edit</button>
        </button>
      ),
    },
    {
      label: "Delete Group",
      accessor: "delete",
      render: (item) => (
        <div
          onClick={() => handleDelete(item.groupId)}
          className="flex gap-2 w-fit px-2 py-1 items-center text-nowrap text-xs text-error-700 bg-error-50 rounded-[4px] font-medium"
        >
          <img src={trash} alt="icon" className="w-3 h-3" />
          <button>Delete</button>
        </div>
      ),
    },
  ];

  const handleDownload = () => {
    if (!groups.length) return;

    const excelData = groups.map((g, index) => ({
      "SL No": pagination.currentPage * pagination.pageSize + index + 1,
      "Group Name": g.groupName,
      "No of Students": g.noOfStudents,
    }));

    exportToExcel(excelData, "Group_List");
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3 justify-end ">
        <PaginationBar
          currentPage={pagination.currentPage}
          pageSize={pagination.pageSize}
          totalItems={pagination.totalElements}
          onPageChange={(newPage) => {
            dispatch(
              setPagination({
                currentPage: newPage,
                pageSize: pagination.pageSize,
              }),
            );
          }}
          onPageSizeChange={(newSize) => {
            dispatch(
              setPagination({
                currentPage: 0,
                pageSize: newSize,
              }),
            );
          }}
        />

        <DownloadButton
          onClick={handleDownload}
          disabled={isLoading || groups.length === 0}
        />
      </div>
      <div className="grid gap-7 bg-white px-6 py-5 rounded-lg">
        <DataTable
          columns={columns}
          data={groups}
          isLoading={isLoading}
          searchFilters={filters}
          onSearchChange={(accessor, value) => {
            dispatch(setFilters({ [accessor]: value }));
            dispatch(
              setPagination({ currentPage: 0, pageSize: pagination.pageSize }),
            );
          }}
        />
      </div>
      {modal}
    </div>
  );
}
